import ChatChannel from "./channels/chat_channel"

const CHAT_SESSION_TTL_MS = 48 * 60 * 60 * 1000
const CHAT_SESSION_TOUCH_KEY = "chatbot_session_touched_at"

class Chatbot {
  constructor() {
    this.sessionId = this.getOrCreateSessionId()
    this.chatChannel = null
    this.isOpen = false
    this.isMinimized = false
    this.firstMessageSent = false
    this.currentKitId = null // ID do kit atual quando o chatbot é usado dentro de uma página de kit
    this.currentSegment = null
    this.contactV2Roots = []
    this.historyMessages = []
    this.historyLoaded = false
    this.streamRevealQueue = Promise.resolve()

    this.initElements()
    this.initEventListeners()
    this.connectToChannel()
    
    // Tenta detectar kit_id da página (se disponível)
    this.detectKitId()
    
    if (this.legacyUiEnabled) {
      this.loadState()
    } else {
      this.isOpen = false
      this.isMinimized = false
      localStorage.removeItem('chatbot_state')
    }
    
    // Garante que a barra está visível inicialmente se não estiver aberto ou minimizado
    if (this.legacyUiEnabled && !this.isOpen && !this.isMinimized && this.inputBar) {
      this.inputBar.style.display = 'flex'
    }
    this.loadHistory()
    this.syncHomeV4Entry()
  }

  isHomeV4Page() {
    return document.body.classList.contains('home-v4-page')
  }

  hasContactV2Modal() {
    return !!document.getElementById('kit-contact-v2-modal') || !!document.getElementById('home-contact-v2-modal')
  }

  syncHomeV4Entry() {
    if (!this.isHomeV4Page()) return
    document.body.classList.toggle('home-v4-chatbot-active', this.isOpen || this.isMinimized)
  }

  initElements() {
    this.inputBar = document.getElementById('chatbot-input-bar')
    this.window = document.getElementById('chatbot-window')
    this.messagesContainer = document.getElementById('chatbot-messages')
    this.input = document.getElementById('chatbot-input')
    this.inputInner = document.getElementById('chatbot-input-inner')
    this.sendBtn = document.getElementById('chatbot-send')
    this.sendBtnInner = document.getElementById('chatbot-send-inner')
    this.toggleBtn = document.getElementById('chatbot-toggle')
    this.fabBtn = document.getElementById('chatbot-fab')
    this.closeBtn = document.getElementById('chatbot-close')
    this.minimizeBtn = document.getElementById('chatbot-minimize')
    this.confirmModal = document.getElementById('chatbot-confirm-modal')
    this.confirmCancelBtn = document.getElementById('chatbot-confirm-cancel')
    this.confirmOkBtn = document.getElementById('chatbot-confirm-ok')
    this.minimizeIcon = this.minimizeBtn?.querySelector('.minimize-icon')
    this.restoreIcon = this.minimizeBtn?.querySelector('.restore-icon')
    this.legacyUiEnabled = !!document.getElementById('chatbot-container')
  }

  initEventListeners() {
    this.fabBtn?.addEventListener('click', () => {
      if (this.isMinimized) {
        this.restoreWindow()
      } else {
        this.openWindow()
      }
    })

    // Toggle window - desabilitado, botão não é mais clicável
    // this.toggleBtn?.addEventListener('click', () => {
    //   if (this.isMinimized) {
    //     this.restoreWindow()
    //   } else {
    //     this.toggleWindow()
    //   }
    // })
    this.minimizeBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      if (this.isMinimized) {
        this.restoreWindow()
      } else {
        this.minimizeWindow()
      }
    })
    this.closeBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      e.preventDefault()
      if (this.showConfirmModal) {
        this.showConfirmModal()
      }
    })
    
    // Confirmação modal
    this.confirmCancelBtn?.addEventListener('click', () => this.hideConfirmModal())
    this.confirmOkBtn?.addEventListener('click', () => {
      this.hideConfirmModal()
      this.closeWindow()
    })
    
    // Fechar modal ao clicar fora
    this.confirmModal?.addEventListener('click', (e) => {
      if (e.target === this.confirmModal) {
        this.hideConfirmModal()
      }
    })
    
    // Restaurar ao clicar no header quando minimizado
    const header = document.querySelector('.chatbot-header')
    if (header) {
      header.addEventListener('click', (e) => {
        // Não restaurar se clicar nos botões ou ações
        if (e.target.closest('.chatbot-header-actions')) return
        if (e.target.closest('.chatbot-close-btn')) return
        if (e.target.closest('.chatbot-minimize-btn')) return
        if (this.isMinimized) {
          this.restoreWindow()
        }
      })
    }

    // Send messages
    this.sendBtn?.addEventListener('click', () => this.sendMessage())
    this.sendBtnInner?.addEventListener('click', () => this.sendMessage())

    // Enter key to send
    this.input?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.sendMessage()
      }
    })

    this.inputInner?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.sendMessage()
      }
    })

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeWindow()
      }
    })

    // Prevenir scroll da página quando fazendo scroll dentro do popup
    this.messagesContainer?.addEventListener('wheel', (e) => {
      const container = this.messagesContainer
      if (!container) return

      const { scrollTop, scrollHeight, clientHeight } = container
      const isScrollingUp = e.deltaY < 0
      const isScrollingDown = e.deltaY > 0
      const isAtTop = scrollTop === 0
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1

      // Se está no topo e tentando rolar para cima, ou no fim e tentando rolar para baixo
      // Previne o scroll da página (não permite scroll além dos limites)
      if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
        e.preventDefault()
      } else {
        // Quando há espaço para rolar, permite o scroll dentro do container
        // Impede que o evento se propague para o document, evitando scroll do site
        e.stopPropagation()
      }
    }, { passive: false })

    // Prevenir scroll do site quando o mouse está sobre a janela do chat
    // Usa um listener no document para capturar eventos que não foram tratados pelo container
    // IMPORTANTE: Deve usar capture: true para interceptar antes do fullpage.js
    this.documentWheelHandler = (e) => {
      if (!this.window || !this.isOpen) return
      
      // Verifica se o mouse está sobre a janela do chat
      const windowRect = this.window.getBoundingClientRect()
      const mouseX = e.clientX
      const mouseY = e.clientY
      const isMouseOverWindow = mouseX >= windowRect.left && mouseX <= windowRect.right && 
                                mouseY >= windowRect.top && mouseY <= windowRect.bottom
      
      // Se o mouse está sobre a janela do chat e o evento não foi tratado pelo messagesContainer
      // Previne o scroll do site (incluindo fullpage.js)
      if (isMouseOverWindow && !e.target.closest('#chatbot-messages')) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    document.addEventListener('wheel', this.documentWheelHandler, { passive: false, capture: true })
    
    // Listener para recalcular posição quando a janela é redimensionada ou quando o conteúdo muda
    this.resizeObserver = new ResizeObserver(() => {
      if (this.isMinimized) {
        this.updateMinimizedPosition()
      }
    })
    
    // Observa mudanças na barra de sumário (se existir)
    const summaryBar = document.querySelector('.kit-footer-summary')
    if (summaryBar) {
      this.resizeObserver.observe(summaryBar)
    }
    
    // Observa mudanças na janela do chat
    if (this.window) {
      this.resizeObserver.observe(this.window)
    }
    
    // Também recalcula quando a janela do navegador é redimensionada
    window.addEventListener('resize', () => {
      if (this.isMinimized) {
        this.updateMinimizedPosition()
      }
    })
  }

  getOrCreateSessionId() {
    let sessionId = localStorage.getItem('chatbot_session_id')
    if (!sessionId || this.isSessionExpired()) {
      sessionId = this.generateSessionId()
      localStorage.setItem('chatbot_session_id', sessionId)
      this.touchSession()
    }
    return sessionId
  }

  isSessionExpired() {
    const touchedAt = Number(localStorage.getItem(CHAT_SESSION_TOUCH_KEY) || 0)
    if (!touchedAt) return Boolean(localStorage.getItem('chatbot_session_id'))
    return Date.now() - touchedAt > CHAT_SESSION_TTL_MS
  }

  isHistoryStale(messages) {
    if (!messages || messages.length === 0) return false

    const last = messages[messages.length - 1]
    const at = Date.parse(last.created_at || last.updated_at || '')
    if (!Number.isFinite(at)) return false
    return Date.now() - at > CHAT_SESSION_TTL_MS
  }

  touchSession() {
    localStorage.setItem(CHAT_SESSION_TOUCH_KEY, String(Date.now()))
  }

  generateSessionId() {
    return 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }

  detectKitId() {
    if (!this.currentKitId) {
      const bodyKitId = document.body?.getAttribute('data-kit-id')
      if (bodyKitId) {
        this.currentKitId = parseInt(bodyKitId, 10)
      } else {
        const kitElement = document.querySelector('[data-kit-id]')
        const kitId = kitElement?.getAttribute('data-kit-id')
        if (kitId) this.currentKitId = parseInt(kitId, 10)
      }
    }

    const segment = document.body?.getAttribute('data-kit-segment')
    if (segment) this.currentSegment = segment
  }

  collectKitSelection() {
    if (!this.currentKitId) return null

    const selection = {}
    const share = document.getElementById('kit-pdf-share')
    if (typeof window.readKitQuoteDefaults === 'function') {
      Object.assign(selection, window.readKitQuoteDefaults(share))
    } else if (share) {
      try {
        Object.assign(selection, JSON.parse(share.getAttribute('data-quote-defaults') || '{}'))
      } catch (error) {
        // ignora JSON inválido
      }
    }

    if (typeof window.kitQuoteSelectionFromForm === 'function') {
      const live = window.kitQuoteSelectionFromForm() || {}
      Object.keys(live).forEach((key) => {
        if (live[key] != null && live[key] !== '') selection[key] = live[key]
      })
    } else {
      Object.assign(selection, this.readSelectionFromDom())
    }

    const segment = this.currentSegment || document.body?.getAttribute('data-kit-segment')
    if (segment) selection.segment = segment

    return this.compactSelection(selection)
  }

  readSelectionFromDom() {
    const checked = (name) => document.querySelector(`input[name="${name}"]:checked`)?.value
    return {
      cpu_id: checked('custom_kit[custom_kit_products_attributes][0][product_id]'),
      vga_id: checked('custom_kit[custom_kit_products_attributes][1][product_id]'),
      mem_id: checked('custom_kit[custom_kit_products_attributes][2][product_id]'),
      ssd_id: checked('custom_kit[custom_kit_products_attributes][3][product_id]'),
      hd_id: checked('custom_kit[custom_kit_products_attributes][4][product_id]'),
      cooler_id: document.getElementById('kit_cooler_product_id')?.value
        || checked('custom_kit[custom_kit_products_attributes][5][product_id]'),
      psu_id: checked('custom_kit[custom_kit_products_attributes][7][product_id]')
        || document.querySelector('input[name="custom_kit[custom_kit_products_attributes][7][product_id]"]')?.value,
      ssd_qty: document.getElementById('ssd_qty')?.value,
      hd_qty: document.getElementById('hd_qty')?.value,
      vga_qty: document.getElementById('vga_qty')?.value,
      color: document.getElementById('colorMode')?.value
    }
  }

  compactSelection(selection) {
    const compact = {}
    Object.keys(selection || {}).forEach((key) => {
      const value = selection[key]
      if (value != null && String(value).trim() !== '') compact[key] = String(value)
    })
    return Object.keys(compact).length ? compact : null
  }

  connectToChannel() {
    this.chatChannel = new ChatChannel(this.sessionId, {
      onConnected: () => {},
      onDisconnected: () => {},
      onSessionId: (newSessionId) => {
        if (newSessionId && newSessionId !== this.sessionId) {
          this.sessionId = newSessionId
          localStorage.setItem('chatbot_session_id', newSessionId)
        }
      },
      onMessage: (data) => {
        this.handleMessage(data)
      }
    })

    this.chatChannel.connect()
  }

  openWindow() {
    if (this.isOpen && !this.isMinimized) return

    if (!this.window) {
      this.window = document.getElementById('chatbot-window')
      if (!this.window) {
        return
      }
    }

    this.isOpen = true
    this.isMinimized = false
    this.window.classList.add('open')
    this.window.classList.remove('minimized')
    
    // Esconde o rodapé de entrada quando o popup está aberto
    if (this.inputBar) {
      this.inputBar.style.display = 'none'
    }
    
    // Troca o ícone
    if (this.minimizeIcon) this.minimizeIcon.style.display = 'block'
    if (this.restoreIcon) this.restoreIcon.style.display = 'none'
    
    // Força o display para garantir que apareça
    this.window.style.display = 'flex'
    this.window.style.opacity = '1'
    this.window.style.transform = 'translateY(0)'
    
    if (this.input) this.input.blur()
    if (this.inputInner) this.inputInner.focus()

    // Remove welcome message se já houver mensagens
    const welcome = this.messagesContainer?.querySelector('.chatbot-welcome')
    if (welcome && this.messagesContainer?.children.length > 1) {
      welcome.remove()
    }

    this.scrollToBottom()
    
    // Salva o estado
    this.saveState()
    this.syncHomeV4Entry()
  }

  minimizeWindow() {
    if (!this.window) return
    
    this.isMinimized = true
    this.isOpen = true
    this.window.classList.add('minimized')
    this.window.classList.remove('open')
    
    // Esconde o rodapé de entrada quando o popup está minimizado
    if (this.inputBar) {
      this.inputBar.style.display = 'none'
    }
    
    // Troca o ícone
    if (this.minimizeIcon) this.minimizeIcon.style.display = 'none'
    if (this.restoreIcon) this.restoreIcon.style.display = 'block'
    
    // Calcula e aplica posicionamento acima da barra de sumário (se existir)
    this.updateMinimizedPosition()
    
    // Salva o estado
    this.saveState()
    this.syncHomeV4Entry()
  }

  updateMinimizedPosition() {
    if (!this.window || !this.isMinimized) return
    
    // Verifica se estamos na página de kits ou na página Monte seu PC
    const isKitsPage = document.body.classList.contains('kits-show-page') ||
      document.body.classList.contains('kit-pro-v2-page')
    const isMonteSeuPcPage = document.body.classList.contains('monte-seu-pc-page')
    
    if (isKitsPage || isMonteSeuPcPage) {
      // Procura pela barra de sumário de configurações
      const summaryBar = document.querySelector('.kit-footer-summary')
      
      if (summaryBar) {
        // Calcula a altura da barra de sumário
        const summaryBarHeight = summaryBar.offsetHeight
        
        // Calcula a altura do chat minimizado
        const chatHeight = this.window.offsetHeight
        
        // Posiciona o chat acima da barra de sumário com um pequeno espaçamento (8px)
        const bottomPosition = summaryBarHeight + 8
        
        // Aplica o posicionamento
        this.window.style.bottom = `${bottomPosition}px`
        this.window.style.transform = 'translateY(0)'
      } else {
        // Se não houver barra de sumário, usa posicionamento padrão
        this.window.style.bottom = ''
        this.window.style.transform = ''
      }
    }
  }

  restoreWindow() {
    if (!this.window) return
    
    this.isMinimized = false
    this.isOpen = true
    this.window.classList.remove('minimized')
    this.window.classList.add('open')
    
    // Esconde o rodapé de entrada quando o popup está aberto
    if (this.inputBar) {
      this.inputBar.style.display = 'none'
    }
    
    // Força o display para garantir que apareça
    this.window.style.display = 'flex'
    this.window.style.opacity = '1'
    this.window.style.transform = 'translateY(0)'
    
    // Troca o ícone
    if (this.minimizeIcon) this.minimizeIcon.style.display = 'block'
    if (this.restoreIcon) this.restoreIcon.style.display = 'none'
    
    if (this.inputInner) this.inputInner.focus()
    this.scrollToBottom()
    
    // Salva o estado
    this.saveState()
    this.syncHomeV4Entry()
  }

  showConfirmModal() {
    if (!this.confirmModal) return
    
    // Garante que a janela está aberta para mostrar o modal
    if (!this.isOpen || this.isMinimized) {
      this.openWindow()
    }
    
    this.confirmModal.style.display = 'flex'
    this.scrollToBottom()
  }

  hideConfirmModal() {
    if (!this.confirmModal) return
    
    this.confirmModal.style.display = 'none'
  }

  closeWindow() {
    if (!this.window) return
    
    this.isOpen = false
    this.isMinimized = false
    this.window.classList.remove('open', 'minimized')
    
    // Mostra o rodapé de entrada quando o popup está fechado
    if (this.inputBar) {
      this.inputBar.style.display = 'flex'
    }
    
    // Remove os estilos inline que foram forçados
    this.window.style.display = ''
    this.window.style.opacity = ''
    this.window.style.transform = ''
    
    if (this.inputInner) this.inputInner.blur()
    
    // Salva o estado antes de limpar
    this.saveState()
    this.syncHomeV4Entry()
    
    // Limpa o histórico
    this.clearHistory()
  }

  clearHistory() {
    // Limpa mensagens da UI
    if (this.messagesContainer) {
      this.messagesContainer.innerHTML = `
        <div class="chatbot-welcome">
          <div class="chatbot-welcome-icon">
            <i class="fas fa-robot"></i>
          </div>
          <p class="chatbot-welcome-text">
            Olá! Sou o assistente virtual da Rocketz. Como posso ajudar você hoje?
          </p>
        </div>
      `
    }

    this.historyMessages = []
    this.historyLoaded = true
    this.streamRevealQueue = Promise.resolve()
    this.syncAllContactV2Roots()

    // Reseta flags
    this.firstMessageSent = false
    
    // Salva o session_id antigo antes de gerar um novo
    const oldSessionId = this.sessionId
    
    // Gera um novo session_id para iniciar uma nova conversa
    this.sessionId = this.generateSessionId()
    localStorage.setItem('chatbot_session_id', this.sessionId)
    this.touchSession()
    this.syncContactV2ClearControls()
    
    // Reconecta ao canal com o novo session_id
    if (this.chatChannel) {
      this.chatChannel.disconnect()
    }
    this.connectToChannel()
    
    // Limpa histórico no servidor usando o session_id antigo
    if (oldSessionId) {
      this.clearServerHistory(oldSessionId)
    }
  }

  clearServerHistory(sessionId) {
    // Chama a API para limpar o histórico no servidor
    fetch(`/chat_messages/clear?session_id=${sessionId}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      }
    })
    .catch(error => {
      // Silently fail - not critical
    })
  }

  toggleWindow() {
    if (this.isOpen && !this.isMinimized) {
      this.minimizeWindow()
    } else {
      this.openWindow()
    }
  }

  getInputValue() {
    // Prioriza o input da janela se estiver aberta, senão usa o input da barra fixa
    const innerValue = this.inputInner?.value?.trim() || ''
    const outerValue = this.input?.value?.trim() || ''
    
    if (this.isOpen && innerValue) {
      return innerValue
    } else if (outerValue) {
      return outerValue
    }
    
    return ''
  }

  clearInput() {
    if (this.inputInner) this.inputInner.value = ''
    if (this.input) this.input.value = ''
  }

  registerContactV2Root(root) {
    if (!root) return

    const ui = {
      root,
      viewportEl: root.querySelector('[data-contact-v2-viewport]'),
      messagesEl: root.querySelector('[data-contact-v2-messages]'),
      form: root.querySelector('[data-contact-v2-form]'),
      input: root.querySelector('[data-contact-v2-input]'),
      suggestions: root.querySelectorAll('[data-contact-v2-suggestion]'),
      stickToBottom: true
    }

    if (!ui.messagesEl) return

    if (!ui.viewportEl) {
      ui.viewportEl = ui.messagesEl.closest('.contact-v2-panel__body') || ui.messagesEl
    }

    const welcomeEl = ui.messagesEl.querySelector('[data-contact-v2-message="assistant"] .contact-v2-chat__text')
    ui.welcomeText = welcomeEl?.textContent?.trim() || ''

    this.contactV2Roots.push(ui)
    this.bindContactV2Viewport(ui)
    this.bindContactV2NewConversation(ui)

    ui.form?.addEventListener('submit', (event) => {
      event.preventDefault()
      const text = ui.input?.value?.trim()
      if (!text) return
      this.sendMessageContent(text)
      if (ui.input) ui.input.value = ''
    })

    ui.suggestions.forEach((button) => {
      button.addEventListener('click', () => {
        this.sendMessageContent(button.textContent.trim())
      })
    })

    if (this.historyLoaded) {
      this.renderContactV2History(ui, this.historyMessages, { preserveWelcome: this.historyMessages.length === 0 })
      this.syncContactV2ClearControls()
    }
  }

  hasStoredConversation() {
    if (this.firstMessageSent) return true
    if (Array.isArray(this.historyMessages) && this.historyMessages.some((msg) => msg.role === 'user')) return true
    return this.contactV2Roots.some((ui) => ui.messagesEl?.querySelector('[data-contact-v2-message="user"]'))
  }

  syncContactV2ClearControls() {
    const show = this.hasStoredConversation()
    this.contactV2Roots.forEach((ui) => {
      ui.root?.querySelectorAll('[data-contact-v2-clear]').forEach((el) => {
        el.hidden = !show
      })
    })
  }

  bindContactV2NewConversation(ui) {
    if (!ui?.root) return

    const confirmEl = ui.root.querySelector('[data-contact-v2-confirm]')
    const cancelBtn = ui.root.querySelector('[data-contact-v2-confirm-cancel]')
    const okBtn = ui.root.querySelector('[data-contact-v2-confirm-ok]')
    const triggers = ui.root.querySelectorAll('[data-contact-v2-new-conversation]')

    const hideConfirm = () => {
      if (!confirmEl) return
      confirmEl.hidden = true
      confirmEl.setAttribute('aria-hidden', 'true')
    }

    const showConfirm = () => {
      if (confirmEl) {
        confirmEl.hidden = false
        confirmEl.setAttribute('aria-hidden', 'false')
        cancelBtn?.focus()
        return
      }

      if (window.confirm('Apagar esta conversa? As mensagens somem desta janela.')) {
        this.startContactV2NewConversation(ui)
      }
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault()
        showConfirm()
      })
    })

    cancelBtn?.addEventListener('click', hideConfirm)
    okBtn?.addEventListener('click', () => {
      hideConfirm()
      this.startContactV2NewConversation(ui)
    })

    confirmEl?.addEventListener('click', (event) => {
      if (event.target === confirmEl) hideConfirm()
    })

    ui.root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && confirmEl && !confirmEl.hidden) {
        event.preventDefault()
        hideConfirm()
      }
    })
  }

  startContactV2NewConversation(ui) {
    this.clearHistory()

    if (ui?.viewportEl) {
      ui.stickToBottom = true
      this.scrollContactV2ToBottom(ui, { force: true })
    }

    if (ui?.input) {
      window.requestAnimationFrame(() => ui.input.focus())
    }
  }

  bindContactV2Viewport(ui) {
    if (!ui?.viewportEl) return

    ui.viewportEl.addEventListener('scroll', () => {
      ui.stickToBottom = this.isContactV2NearBottom(ui.viewportEl)
    }, { passive: true })

    ui.viewportEl.addEventListener('wheel', (event) => {
      const viewport = ui.viewportEl
      const { scrollTop, scrollHeight, clientHeight } = viewport
      const isScrollingUp = event.deltaY < 0
      const isScrollingDown = event.deltaY > 0
      const isAtTop = scrollTop <= 0
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1

      if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
        event.preventDefault()
      } else {
        event.stopPropagation()
      }
    }, { passive: false })
  }

  isContactV2NearBottom(viewportEl, threshold = 72) {
    if (!viewportEl) return true

    const distance = viewportEl.scrollHeight - viewportEl.scrollTop - viewportEl.clientHeight
    return distance <= threshold
  }

  syncAllContactV2Roots(options = {}) {
    this.contactV2Roots.forEach((ui) => {
      this.renderContactV2History(ui, this.historyMessages, options)
    })
  }

  renderContactV2History(ui, messages, options = {}) {
    if (!ui?.messagesEl) return

    const { preserveWelcome = false } = options

    if (!messages || messages.length === 0) {
      if (preserveWelcome) return

      ui.messagesEl.innerHTML = ''
      if (ui.welcomeText) {
        this.addContactV2MessageTo(ui, 'assistant', ui.welcomeText)
      }
      return
    }

    ui.messagesEl.innerHTML = ''
    messages.forEach((msg) => {
      this.addContactV2MessageTo(ui, msg.role, msg.content)
    })
    this.scrollContactV2ToBottom(ui, { force: true })
  }

  addContactV2Message(role, content, options = {}) {
    this.contactV2Roots.forEach((ui) => {
      this.addContactV2MessageTo(ui, role, content, options)
    })
  }

  addContactV2MessageTo(ui, role, content, options = {}) {
    if (!ui?.messagesEl || !content || content.trim() === '') return

    const bubble = document.createElement('div')
    bubble.className = `contact-v2-chat__message contact-v2-chat__message--${role}`
    bubble.setAttribute('data-contact-v2-message', role)

    const roleLabel = document.createElement('span')
    roleLabel.className = 'contact-v2-chat__role'
    roleLabel.textContent = role === 'assistant' ? 'Assistente' : 'Você'

    const textEl = document.createElement('p')
    textEl.className = 'contact-v2-chat__text'

    if (role === 'assistant') {
      textEl.innerHTML = this.processLinks(content)
    } else {
      textEl.textContent = content
    }

    bubble.appendChild(roleLabel)
    bubble.appendChild(textEl)
    ui.messagesEl.appendChild(bubble)

    const forceScroll = options.forceScroll || role === 'user'
    this.scrollContactV2ToBottom(ui, { force: forceScroll })
  }

  scrollContactV2ToBottom(ui, options = {}) {
    const viewport = ui?.viewportEl
    if (!viewport) return

    const { force = false } = options

    if (!force && ui.stickToBottom === false) return

    if (!force && !this.isContactV2NearBottom(viewport)) {
      ui.stickToBottom = false
      return
    }

    ui.stickToBottom = true

    window.requestAnimationFrame(() => {
      viewport.scrollTop = viewport.scrollHeight
    })
  }

  sendMessage() {
    const content = this.getInputValue()
    if (!content) return

    this.sendMessageContent(content, { openWindow: true })
    this.clearInput()
  }

  sendMessageContent(content, options = {}) {
    const { openWindow = false } = options

    if (!content) return

    if (content.length > 10000) {
      this.addMessage('assistant', 'A mensagem é muito longa. Máximo de 10.000 caracteres.')
      return
    }

    const sanitizedContent = this.sanitizeContent(content)

    if (!sanitizedContent || sanitizedContent.trim().length === 0) {
      this.addMessage('assistant', 'A mensagem não pode estar vazia.')
      return
    }

    if (openWindow && (!this.firstMessageSent || !this.isOpen)) {
      if (!this.firstMessageSent) {
        this.firstMessageSent = true
      }

      try {
        this.openWindow()
      } catch (error) {
        // Error opening window - silently fail
      }
    } else if (!this.firstMessageSent) {
      this.firstMessageSent = true
    }

    this.addMessage('user', sanitizedContent)
    this.sendToServer(sanitizedContent)
    this.touchSession()
    this.syncContactV2ClearControls()
  }

  sanitizeContent(content) {
    // Remove tags HTML perigosas, mantém apenas texto
    const div = document.createElement('div')
    div.textContent = content
    return div.textContent || div.innerText || ''
  }

  sendToServer(content) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    
    if (!csrfToken) {
      this.addMessage('assistant', 'Erro de segurança. Por favor, recarregue a página.')
      return
    }
    
    // Tenta detectar o kit_id novamente antes de enviar (caso tenha sido definido após a inicialização)
    if (!this.currentKitId) {
      this.detectKitId()
    }
    
    const payload = {
      content: content,
      session_id: this.sessionId
    }
    
    if (this.currentKitId) {
      payload.kit_id = this.currentKitId
      const selection = this.collectKitSelection()
      if (selection) payload.selection = selection
    }
    
    fetch('/chat_messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(payload)
    })
    .then(response => {
      if (!response.ok) {
        // Trata diferentes códigos de erro
        if (response.status === 429) {
          return response.json().then(data => {
            throw new Error(data.errors?.[0] || 'Muitas mensagens enviadas. Por favor, aguarde um momento.')
          })
        }
        return response.json().then(data => {
          throw new Error(data.errors?.[0] || data.error || 'Erro ao enviar mensagem.')
        })
      }
      return response.json()
    })
    .then(data => {
      if (data.errors && data.errors.length > 0) {
        this.addMessage('assistant', data.errors[0] || 'Desculpe, ocorreu um erro ao enviar sua mensagem.')
      }
    })
    .catch(error => {
      // Error sending message
      const errorMessage = error.message || 'Desculpe, ocorreu um erro ao enviar sua mensagem.'
      this.addMessage('assistant', errorMessage)
    })
  }

  handleMessage(data) {
    if (data.role === 'assistant') {
      this.addMessage('assistant', data.content, { animate: true })
    }
  }

  addMessage(role, content, options = {}) {
    if (!content || content.trim() === '') return

    if (options.animate && role === 'assistant') {
      this.addAnimatedAssistantMessage(content)
      return
    }

    if (this.legacyUiEnabled) {
      this.appendChatbotMessage(role, content)
    }

    const scrollOptions = role === 'user' ? { forceScroll: true } : {}
    this.addContactV2Message(role, content, scrollOptions)
  }

  addAnimatedAssistantMessage(content) {
    const targets = []

    const chatbotContent = this.createChatbotAssistantBubble()
    if (chatbotContent) {
      targets.push({
        element: chatbotContent,
        onScroll: () => this.scrollToBottom()
      })
    }

    this.contactV2Roots.forEach((ui) => {
      const textEl = this.createContactV2AssistantBubble(ui)
      if (textEl) {
        targets.push({
          element: textEl,
          onScroll: () => this.scrollContactV2ToBottom(ui, { force: ui.stickToBottom !== false })
        })
      }
    })

    if (targets.length === 0) return

    this.streamRevealQueue = this.streamRevealQueue
      .then(() => this.revealAssistantText(content, targets))
      .catch(() => {})
  }

  createChatbotAssistantBubble() {
    const welcome = this.messagesContainer?.querySelector('.chatbot-welcome')
    if (welcome) welcome.remove()

    if (!this.messagesContainer) return null

    const messageDiv = document.createElement('div')
    messageDiv.className = 'chatbot-message assistant'

    const contentDiv = document.createElement('div')
    contentDiv.className = 'chatbot-message-content'

    const timeDiv = document.createElement('div')
    timeDiv.className = 'chatbot-message-time'
    timeDiv.textContent = this.formatTime(new Date())

    messageDiv.appendChild(contentDiv)
    messageDiv.appendChild(timeDiv)
    this.messagesContainer.appendChild(messageDiv)

    return contentDiv
  }

  createContactV2AssistantBubble(ui) {
    if (!ui?.messagesEl) return null

    const bubble = document.createElement('div')
    bubble.className = 'contact-v2-chat__message contact-v2-chat__message--assistant'
    bubble.setAttribute('data-contact-v2-message', 'assistant')

    const roleLabel = document.createElement('span')
    roleLabel.className = 'contact-v2-chat__role'
    roleLabel.textContent = 'Assistente'

    const textEl = document.createElement('p')
    textEl.className = 'contact-v2-chat__text'

    bubble.appendChild(roleLabel)
    bubble.appendChild(textEl)
    ui.messagesEl.appendChild(bubble)

    return textEl
  }

  revealAssistantText(fullText, targets) {
    return new Promise((resolve) => {
      const length = fullText.length
      if (length === 0) {
        resolve()
        return
      }

      const minDuration = 400
      const maxDuration = 2800
      const duration = Math.min(maxDuration, Math.max(minDuration, length * 11))
      const start = performance.now()

      const finish = () => {
        targets.forEach((target) => {
          target.element.innerHTML = this.processLinks(fullText)
          target.onScroll?.()
        })
        resolve()
      }

      const step = (now) => {
        const progress = Math.min(1, (now - start) / duration)
        const eased = 1 - Math.pow(1 - progress, 2.1)
        const index = progress >= 1 ? length : Math.floor(eased * length)
        const slice = fullText.slice(0, index)

        targets.forEach((target) => {
          target.element.textContent = slice
          target.onScroll?.()
        })

        if (progress < 1) {
          requestAnimationFrame(step)
        } else {
          finish()
        }
      }

      requestAnimationFrame(step)
    })
  }

  appendChatbotMessage(role, content) {
    const welcome = this.messagesContainer?.querySelector('.chatbot-welcome')
    if (welcome) welcome.remove()

    if (!this.messagesContainer) return

    const messageDiv = document.createElement('div')
    messageDiv.className = `chatbot-message ${role}`

    const contentDiv = document.createElement('div')
    contentDiv.className = 'chatbot-message-content'
    contentDiv.innerHTML = this.processLinks(content)

    const timeDiv = document.createElement('div')
    timeDiv.className = 'chatbot-message-time'
    timeDiv.textContent = this.formatTime(new Date())

    messageDiv.appendChild(contentDiv)
    messageDiv.appendChild(timeDiv)
    this.messagesContainer.appendChild(messageDiv)

    this.scrollToBottom()
  }

  decodeBasicHtmlEntities(text) {
    return String(text)
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#0*39;/g, "'")
      .replace(/&#x27;/gi, "'")
  }

  isAllowedRelativePath(url) {
    if (!url || typeof url !== 'string') return false

    const path = url.trim().split(/[?#]/)[0].replace(/\/+$/, '') || '/'
    if (path === '/' || !path.startsWith('/')) return false
    if (!/^\/[a-z0-9/_-]+$/.test(path)) return false

    const exact = {
      '/contato': true,
      '/garantia': true,
      '/sobre': true,
      '/privacidade': true,
      '/faq': true,
      '/artigos': true
    }
    if (exact[path]) return true

    const rest = path.slice(1)
    return rest.includes('-') || rest.includes('/')
  }

  processLinks(content) {
    if (!content) return ''

    content = this.decodeBasicHtmlEntities(content)
    
    // Escapa HTML para prevenir XSS
    const escapeHtml = (text) => {
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    }
    
    // Escapa atributos HTML (para URLs em href)
    const escapeHtmlAttribute = (text) => {
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    }
    
    // Valida e sanitiza URL para prevenir XSS via javascript: ou data: URLs
    const validateAndSanitizeUrl = (url) => {
      if (!url) return null
      
      url = url.trim().replace(/[.,;:!?)]+$/, '')
      
      // Rejeita URLs perigosas (javascript:, data:, vbscript:, etc)
      const dangerousProtocols = /^(javascript|data|vbscript|file|about):/i
      if (dangerousProtocols.test(url)) {
        return null
      }
      
      // Aceita apenas http, https ou caminhos relativos
      if (url.startsWith('http://') || url.startsWith('https://')) {
        try {
          const urlObj = new URL(url)
          // Garante que o protocolo é http ou https
          if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
            return null
          }
          // Valida que é do domínio da Rocketz (aceita www.rocketz.com.br ou rocketz.com.br)
          const hostname = urlObj.hostname.toLowerCase()
          if (hostname !== 'rocketz.com.br' && hostname !== 'www.rocketz.com.br' && !hostname.endsWith('.rocketz.com.br')) {
            return null
          }
          return urlObj.href
        } catch (e) {
          // Se não conseguir fazer parse, rejeita
          return null
        }
      }
      
      // Caminhos relativos: só rotas reais da loja, não "Revit/IFC"
      if (url.startsWith('/')) {
        if (/[<>"']/.test(url)) return null
        if (!this.isAllowedRelativePath(url)) return null
        return url
      }
      
      return null
    }
    
    // Primeiro, escapa todo o conteúdo
    let processed = escapeHtml(content)
    
    // Função auxiliar para verificar se uma posição está dentro de uma tag HTML
    const isInsideHtmlTag = (text, position) => {
      const before = text.substring(0, position)
      const lastTagOpen = before.lastIndexOf('<')
      const lastTagClose = before.lastIndexOf('>')
      return lastTagOpen > lastTagClose
    }
    
    // Padrões para detectar links:
    // 1. URLs markdown [texto](url) - processar primeiro
    // 2. URLs completas (http/https) do domínio rocketz.com.br
    // 3. URLs que começam com / (caminhos relativos)
    
    // Processa links markdown primeiro: [texto](url)
    processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, linkText, url) => {
      const sanitizedUrl = validateAndSanitizeUrl(this.decodeBasicHtmlEntities(url))
      if (sanitizedUrl) {
        return `<a href="${escapeHtmlAttribute(sanitizedUrl)}" target="_blank" rel="noopener noreferrer" style="color: #2a5298; text-decoration: underline; font-weight: 600;">${linkText}</a>`
      }
      return linkText
    })
    
    // Processa URLs diretas (http/https) do domínio rocketz.com.br
    const httpUrls = []
    // Regex melhorado para capturar URLs do rocketz.com.br (com ou sem www, com parâmetros, etc)
    const httpUrlRegex = /(https?:\/\/(?:www\.)?rocketz\.com\.br[^\s<>"']*)/gi
    let httpMatch
    
    while ((httpMatch = httpUrlRegex.exec(processed)) !== null) {
      if (!isInsideHtmlTag(processed, httpMatch.index)) {
        const sanitizedUrl = validateAndSanitizeUrl(httpMatch[1])
        if (sanitizedUrl) {
          httpUrls.push({
            index: httpMatch.index,
            length: httpMatch[0].length,
            url: sanitizedUrl
          })
        }
      }
    }
    
    // Substitui URLs HTTP de trás para frente para manter índices corretos
    for (let i = httpUrls.length - 1; i >= 0; i--) {
      const item = httpUrls[i]
      const before = processed.substring(0, item.index)
      const after = processed.substring(item.index + item.length)
      const escapedUrl = escapeHtmlAttribute(item.url)
      processed = before + `<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer" style="color: #2a5298; text-decoration: underline; font-weight: 600;">${escapeHtml(item.url)}</a>` + after
    }
    
    // Caminhos relativos só no início do token: "veja /pc-gamer/tempest", não "Revit/IFC"
    const relativeUrls = []
    const relativeUrlRegex = /(^|[\s(])(\/[a-z0-9][^\s<>"']*)/g
    let relMatch
    
    while ((relMatch = relativeUrlRegex.exec(processed)) !== null) {
      const slashIndex = relMatch.index + relMatch[1].length
      if (!isInsideHtmlTag(processed, slashIndex)) {
        const sanitizedUrl = validateAndSanitizeUrl(relMatch[2])
        if (sanitizedUrl) {
          relativeUrls.push({
            index: relMatch.index,
            length: relMatch[0].length,
            prefix: relMatch[1],
            url: sanitizedUrl
          })
        }
      }
    }
    
    // Substitui URLs relativas de trás para frente para manter índices corretos
    for (let i = relativeUrls.length - 1; i >= 0; i--) {
      const item = relativeUrls[i]
      const before = processed.substring(0, item.index)
      const after = processed.substring(item.index + item.length)
      const escapedUrl = escapeHtmlAttribute(item.url)
      processed = before + item.prefix + `<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer" style="color: #2a5298; text-decoration: underline; font-weight: 600;">${escapeHtml(item.url)}</a>` + after
    }
    
    // Preserva quebras de linha
    processed = processed.replace(/\n/g, '<br>')
    
    return processed
  }

  isInternalLink(url) {
    if (!url) return false
    
    // Remove espaços e caracteres de pontuação no final
    url = url.trim().replace(/[.,;:!?]+$/, '')
    
    // Rejeita URLs perigosas
    const dangerousProtocols = /^(javascript|data|vbscript|file|about):/i
    if (dangerousProtocols.test(url)) {
      return false
    }
    
    // Verifica se é um caminho relativo (começa com /)
    if (url.startsWith('/')) {
      return true
    }
    
    // Verifica se contém o domínio da Rocketz e usa protocolo seguro
    if (url.includes('rocketz.com.br') && (url.startsWith('http://') || url.startsWith('https://'))) {
      return true
    }
    
    return false
  }

  normalizeUrl(url) {
    if (!url) return '#'
    
    // Remove espaços e caracteres de pontuação no final
    url = url.trim().replace(/[.,;:!?]+$/, '')
    
    // Rejeita URLs perigosas
    const dangerousProtocols = /^(javascript|data|vbscript|file|about):/i
    if (dangerousProtocols.test(url)) {
      return '#'
    }
    
    // Se já é uma URL completa, valida e retorna
    if (url.startsWith('http://') || url.startsWith('https://')) {
      // Valida que é do domínio da Rocketz
      if (!url.includes('rocketz.com.br')) {
        return '#'
      }
      try {
        const urlObj = new URL(url)
        if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
          return '#'
        }
        return urlObj.href
      } catch (e) {
        return '#'
      }
    }
    
    // Se é um caminho relativo, valida e retorna
    if (url.startsWith('/')) {
      // Valida que não contém caracteres perigosos
      if (/[<>"']/.test(url)) {
        return '#'
      }
      return url
    }
    
    // Caso contrário, trata como caminho relativo (se válido)
    if (!/[<>"']/.test(url)) {
      return '/' + url
    }
    
    return '#'
  }

  scrollToBottom() {
    setTimeout(() => {
      if (this.messagesContainer) {
        this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight
      }
    }, 100)
  }

  formatTime(date) {
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  }

  saveState() {
    if (!this.legacyUiEnabled) return

    const state = {
      isOpen: this.isOpen,
      isMinimized: this.isMinimized
    }
    localStorage.setItem('chatbot_state', JSON.stringify(state))
  }

  loadState() {
    if (!this.legacyUiEnabled) return

    try {
      const savedState = localStorage.getItem('chatbot_state')
      if (savedState) {
        const state = JSON.parse(savedState)
        this.isOpen = state.isOpen || false
        this.isMinimized = state.isMinimized || false
        
        // Aplica o estado visualmente se os elementos existirem
        // Usa setTimeout para garantir que o DOM esteja completamente renderizado
        setTimeout(() => {
          if (this.window) {
            if (this.isMinimized) {
              this.window.classList.add('minimized')
              this.window.classList.remove('open')
              this.window.style.display = 'flex'
              
              // Esconde o rodapé de entrada quando o popup está minimizado
              if (this.inputBar) {
                this.inputBar.style.display = 'none'
              }
              
              // Troca o ícone
              if (this.minimizeIcon) this.minimizeIcon.style.display = 'none'
              if (this.restoreIcon) this.restoreIcon.style.display = 'block'
              
              // Calcula e aplica posicionamento acima da barra de sumário (se existir)
              // Usa um pequeno delay para garantir que elementos da página estejam renderizados
              setTimeout(() => {
                this.updateMinimizedPosition()
              }, 100)
            } else if (this.isOpen) {
              this.window.classList.add('open')
              this.window.classList.remove('minimized')
              this.window.style.display = 'flex'
              this.window.style.opacity = '1'
              this.window.style.transform = 'translateY(0)'
              
              // Esconde o rodapé de entrada quando o popup está aberto
              if (this.inputBar) {
                this.inputBar.style.display = 'none'
              }
              
              // Troca o ícone
              if (this.minimizeIcon) this.minimizeIcon.style.display = 'block'
              if (this.restoreIcon) this.restoreIcon.style.display = 'none'
            } else {
              // Fechado
              this.window.classList.remove('open', 'minimized')
              this.window.style.display = ''
              this.window.style.opacity = ''
              this.window.style.transform = ''
              
              // Mostra o rodapé de entrada quando o popup está fechado
              if (this.inputBar) {
                this.inputBar.style.display = 'flex'
              }
            }
          }
          this.syncHomeV4Entry()
        }, 0)
      } else {
        this.syncHomeV4Entry()
      }
    } catch (error) {
      // Se houver erro ao carregar o estado, usa valores padrão
      console.error('Erro ao carregar estado do chatbot:', error)
    }
  }

  loadHistory() {
    fetch(`/chat_messages?session_id=${this.sessionId}`, {
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(messages => {
      if (this.isHistoryStale(messages)) {
        this.clearHistory()
        return
      }

      this.historyMessages = messages || []
      this.historyLoaded = true
      this.syncAllContactV2Roots({ preserveWelcome: this.historyMessages.length === 0 })
      this.syncContactV2ClearControls()

      if (messages && messages.length > 0) {
        this.firstMessageSent = true

        if (this.legacyUiEnabled) {
          const welcome = this.messagesContainer?.querySelector('.chatbot-welcome')
          if (welcome) {
            welcome.remove()
          }

          messages.forEach(msg => {
            this.appendChatbotMessage(msg.role, msg.content)
          })

          if (this.isMinimized) {
            if (this.window) {
              this.window.classList.add('minimized')
              this.window.classList.remove('open')
              this.window.style.display = 'flex'
              if (this.inputBar) {
                this.inputBar.style.display = 'none'
              }
              if (this.minimizeIcon) this.minimizeIcon.style.display = 'none'
              if (this.restoreIcon) this.restoreIcon.style.display = 'block'
              setTimeout(() => {
                this.updateMinimizedPosition()
              }, 100)
            }
          } else if (!this.isOpen && !this.hasContactV2Modal()) {
            this.openWindow()
          }
        }
      } else if (this.legacyUiEnabled && !this.isOpen && !this.isMinimized && this.inputBar) {
        this.inputBar.style.display = 'flex'
      }
    })
    .catch(error => {
      this.historyMessages = []
      this.historyLoaded = true
      this.syncAllContactV2Roots({ preserveWelcome: true })
      this.syncContactV2ClearControls()

      if (this.legacyUiEnabled && !this.isOpen && !this.isMinimized && this.inputBar) {
        this.inputBar.style.display = 'flex'
      }
    })
  }
}

// Inicializa quando o DOM estiver pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.chatbot = new Chatbot()
  })
} else {
  // DOM já está pronto
  window.chatbot = new Chatbot()
}

export default Chatbot

